import {
    type PlantDevice,
    PALETTE_NAMES
} from "./model";
import {
    SCREEN_SIZES,
    UI_SCALE
} from "./screens";
import {
    type Projection,
    fitProjection,
    planOutline,
    roundedPlan,
    radialTicks,
    type Point3,
    boxCorners,
    sideMatrix,
    circleRing,
    lineOnTop,
    type Plane,
    insetPlan,
    type Plan,
    pointsAt,
    solidSvg,
    cylinder,
    sideArc,
    depthOf,
    dotsSvg,
    lineSvg,
    corners,
    haloOf,
    pathOf,
    slabOf,
    onTop,
    knurl,
    ring,
    iso
} from "../../iso/isoKit";
import {
    frameAlong,
    sphereOf,
    solidOf,
    frameOf,
    prismOf,
    scaleOf,
    toneOf,
    fillet,
    disc
} from "../../iso/lathe";
import {
    tubePieces
} from "../../iso/tube";

export const VIEW_WIDTH = 680;

export const VIEW_HEIGHT = 470;

const PLATE: Plan = {
    x: -118,
    y: -118,
    d: 258,
    w: 258,
    r: 10
};

const PLATE_DEPTH = 12;

const PIPE_Z = 10;

const DOCK_TOP = 16;

const MAIN_RADIUS = 2.8;

const BRANCH_RADIUS = 2.4;

const TANK: Plane = [
    -62,
    -62
];

const TANK_RADIUS = 28;

const TANK_BOTTOM = 5;

const TANK_TOP = 88;

const LID_TOP = 91.2;

const CANISTER_RING = 19;

const CANISTER_ANGLES = [
    135,
    225,
    315
];

const REGULATOR: Plane = [
    -24,
    -24
];

const TEE: Point3 = [
    -6,
    -6,
    PIPE_Z
];

const TEE_RADIUS = 4.6;

const DIAGONAL: Point3 = [
    Math.SQRT1_2,
    Math.SQRT1_2,
    0
];

const PHONE_FACE = -76;

const PHONE_Y = 66;

const MONITOR_FACE = -69;

const MONITOR_X = 65;

export const P: Projection = fitProjection([
    ...boxCorners(PLATE, -PLATE_DEPTH - 5, 0),
    [
        TANK[0],
        TANK[1],
        LID_TOP + 22
    ],
    [
        PHONE_FACE,
        PHONE_Y - 25,
        120
    ],
    [
        PHONE_FACE,
        PHONE_Y + 25,
        120
    ],
    [
        MONITOR_X - 64,
        MONITOR_FACE,
        118
    ],
    [
        MONITOR_X + 64,
        MONITOR_FACE,
        118
    ]
], VIEW_WIDTH, VIEW_HEIGHT, {
    pad: 22
});

const K = scaleOf(P);

const add = (a: Point3, b: Point3, scale = 1): Point3 => [
    a[0] + b[0] * scale,
    a[1] + b[1] * scale,
    a[2] + b[2] * scale
];

const byDepth = <T>(items: Array<T>, at: (item: T) => Point3) => [...items].sort((a, b) => depthOf(at(a), P) - depthOf(at(b), P));

const screwAt = (x: number, y: number, z: number) => lineSvg(ring(x, y, 1.6, z, P, 16), {
    tone: "lo"
}) + dotsSvg(pointsAt([
    [
        x,
        y,
        z
    ]
], P), {
    size: 0.5
});

const dotAt = (point: Point3, size = 0.45) => dotsSvg(pointsAt([point], P), {
    size
});

const radians = (degrees: number) => (degrees * Math.PI) / 180;

const onRing = (centre: Plane, radius: number, degrees: number, z: number): Point3 => [
    centre[0] + radius * Math.cos(radians(degrees)),
    centre[1] + radius * Math.sin(radians(degrees)),
    z
];

const base = () => {
    const feet = byDepth(corners(PLATE, 22), ([
        x,
        y
    ]): Point3 => [
        x,
        y,
        0
    ]).map(([
        x,
        y
    ]) => solidSvg(cylinder(x, y, 7, -PLATE_DEPTH - 5, 1.6, P, 28), {
        tone: "lo"
    }) + solidSvg(cylinder(x, y, 5.4, -PLATE_DEPTH - 3.4, 3.4, P, 28), {
        tone: "mid"
    })).join("");

    return [
        `<path class="iso-halo" d="${haloOf(PLATE, -PLATE_DEPTH, PLATE_DEPTH, P)}"/>`,
        feet,
        solidSvg(slabOf(PLATE, -PLATE_DEPTH, PLATE_DEPTH, P, 8, 1.6), {
            tone: "mid"
        }),
        lineSvg(planOutline(insetPlan(PLATE, 5), 0, P), {
            tone: "faint"
        }),
        corners(PLATE, 10).map(([
            x,
            y
        ]) => screwAt(x, y, 0)).join("")
    ].join("");
};

const DRUM_TOP = 30;

const GLASS_TOP = 82;

export const LIQUID_BOTTOM = DRUM_TOP;

export const LIQUID_TOP = 70;

const LIQUID_RADIUS = TANK_RADIUS - 1.4;

const TIE_ANGLES = [
    -12,
    102
];

const tankBody = () => {
    const seams = [
        12,
        15
    ].map((z) => sideArc(TANK[0], TANK[1], TANK_RADIUS, z, P, 72)).join("");

    const plinthDots = Array.from({
        length: 12
    }, (_, index) => onRing(TANK, TANK_RADIUS + 2.6, index * 30 + 15, TANK_BOTTOM));

    const outlet = frameAlong(onRing(TANK, TANK_RADIUS - 0.6, 45, PIPE_Z), DIAGONAL);

    return [
        solidSvg(cylinder(TANK[0], TANK[1], TANK_RADIUS + 5, 0, TANK_BOTTOM, P, 72, 0.6), {
            tone: "mid"
        }),
        dotsSvg(pointsAt(plinthDots, P), {
            size: 0.5
        }),
        solidSvg(cylinder(TANK[0], TANK[1], TANK_RADIUS, TANK_BOTTOM, DRUM_TOP - TANK_BOTTOM, P, 72, 0.6), {
            inner: lineSvg(seams, {
                tone: "faint"
            }),
            tone: "mid"
        }),
        solidSvg(disc(0, 2.6, 4.4, outlet, P), {
            tone: "mid"
        })
    ].join("");
};

const backArc = (radius: number, z: number) => {
    const points = Array.from({
        length: 49
    }, (_, index) => onRing(TANK, radius, 45 + 90 + (index / 48) * 180, z));

    return pathOf(points.map((point) => iso(point, P)));
};

const glassPaths = cylinder(TANK[0], TANK[1], TANK_RADIUS - 0.4, DRUM_TOP, GLASS_TOP - DRUM_TOP, P, 72);

const glassBack = () => solidSvg(glassPaths, {
    className: "plant-glass-body",
    crease: "none",
    tone: "mid"
}) + lineSvg(backArc(TANK_RADIUS - 0.4, GLASS_TOP - 0.2), {
    tone: "faint"
});

export const liquidSvg = (bottom: number, top: number) => top - bottom < 0.05 ? "" : solidSvg(cylinder(TANK[0], TANK[1], LIQUID_RADIUS, bottom, top - bottom, P, 72), {
    className: "plant-dye plant-liquid",
    crease: "lo",
    tone: "lo"
});

/**
 * The tank while a new dye comes in: a stream from the selector, rings on the surface,
 * and the new colour mixing down through the column.
 */
export const pourSvg = (share: number) => {
    const depth = LIQUID_TOP - LIQUID_BOTTOM;

    const mixed = depth * Math.min(1, Math.max(0, (share - 0.12) / 0.88));

    const layer = liquidSvg(LIQUID_TOP - mixed, LIQUID_TOP);

    const isPouring = share < 0.8;

    const stream = isPouring ? `<path class="plant-stream" d="${pathOf([
        iso([
            TANK[0],
            TANK[1],
            GLASS_TOP
        ], P),
        iso([
            TANK[0],
            TANK[1],
            LIQUID_TOP
        ], P)
    ])}"/>` : "";

    const ripples = isPouring ? [
        0,
        0.5
    ].map((phase) => {
        const wave = (share * 3 + phase) % 1;

        return `<path class="plant-ripple" opacity="${(1 - wave).toFixed(2)}" d="${onTop(circleRing(TANK[0], TANK[1], 2 + wave * (LIQUID_RADIUS - 6), 48), LIQUID_TOP, P, true)}"/>`;
    }).join("") : "";

    return layer + stream + ripples;
};

const BUBBLES: Array<[number, number, number]> = [
    [
        30,
        16,
        34
    ],
    [
        58,
        9,
        38
    ],
    [
        72,
        19,
        33
    ],
    [
        18,
        6,
        41
    ],
    [
        44,
        21,
        36
    ],
    [
        64,
        13,
        43
    ]
];

export const BUBBLES_SVG = BUBBLES.map(([
    degrees,
    radius,
    z
], index) => {
    const [
        x,
        y
    ] = iso(onRing(TANK, radius, degrees, z), P);

    const [, top] = iso(onRing(TANK, radius, degrees, LIQUID_TOP - 1.2), P);

    return `<circle class="plant-bubble" cx="${x.toFixed(2)}" cy="${y.toFixed(2)}" r="${(0.5 + (index % 3) * 0.18).toFixed(2)}" style="--rise: ${(top - y).toFixed(2)}px; animation-delay: ${index * 530}ms"/>`;
}).join("");

const glassFront = () => {
    const glints = [
        62,
        71
    ].map((degrees, index) => lineSvg(pathOf([
        iso(onRing(TANK, TANK_RADIUS - 0.4, degrees, DRUM_TOP + 3), P),
        iso(onRing(TANK, TANK_RADIUS - 0.4, degrees, GLASS_TOP - 4 - index * 10), P)
    ]), {
        className: "plant-glint",
        free: true
    })).join("");

    const rods = byDepth(TIE_ANGLES, (degrees) => onRing(TANK, TANK_RADIUS + 2.4, degrees, 0)).map((degrees) => {
        const [
            x,
            y
        ] = onRing(TANK, TANK_RADIUS + 2.4, degrees, 0);

        return solidSvg(cylinder(x, y, 0.9, DRUM_TOP, GLASS_TOP - DRUM_TOP, P, 12), {
            tone: "mid"
        });
    }).join("");

    return glints + lineSvg(glassPaths.outline, {
        tone: "mid"
    }) + rods + solidSvg(cylinder(TANK[0], TANK[1], TANK_RADIUS + 0.8, GLASS_TOP, TANK_TOP - GLASS_TOP, P, 72, 0.5), {
        tone: "mid"
    });
};

const canisterAt = (degrees: number) => onRing(TANK, CANISTER_RING, degrees, LID_TOP);

const lid = () => {
    const lidDots = Array.from({
        length: 16
    }, (_, index) => onRing(TANK, TANK_RADIUS - 0.4, index * 22.5, LID_TOP));

    return solidSvg(cylinder(TANK[0], TANK[1], TANK_RADIUS + 1.6, TANK_TOP, LID_TOP - TANK_TOP, P, 72, 0.6), {
        tone: "mid"
    }) + dotsSvg(pointsAt(lidDots, P), {
        size: 0.45
    });
};

export const canisterSvg = (index: number) => {
    const [
        x,
        y
    ] = canisterAt(CANISTER_ANGLES[index]!);

    return [
        solidSvg(cylinder(x, y, 7.2, LID_TOP, 2, P, 36, 0.4), {
            tone: "mid"
        }),
        `<g class="plant-dye" style="--dye: var(--plant-canister-${index})">${solidSvg(cylinder(x, y, 6.3, LID_TOP + 2, 14, P, 36), {
            crease: "none",
            tone: "hi"
        })}</g>`,
        lineSvg(sideArc(x, y, 6.3, LID_TOP + 12.4, P, 24), {
            tone: "faint"
        }),
        solidSvg(cylinder(x, y, 6.9, LID_TOP + 16, 3.2, P, 36, 0.4), {
            tone: "mid"
        }),
        lineSvg(knurl(x, y, 6.9, LID_TOP + 16.4, LID_TOP + 18.8, 28, P), {
            tone: "lo"
        })
    ].join("");
};

const feedLineOf = (index: number) => {
    const degrees = CANISTER_ANGLES[index]!;

    const from = onRing(TANK, CANISTER_RING - 7.2, degrees, LID_TOP + 1.3);

    const to = onRing(TANK, 5.4, degrees, LID_TOP + 1.3);

    return tubePieces([
        from,
        to
    ], 1.1, P, {
        maxLength: 400
    }).map((piece) => `<g class="tb plant-feed" style="--dye: var(--plant-canister-${index})"><path class="tb-body" d="${piece.body}"/><path class="iso-line tb-edge" data-tone="mid" d="${piece.edges}"/></g>`).join("");
};

export const CANISTER_COUNT = PALETTE_NAMES.length;

const canisters = () => {
    const order = byDepth(CANISTER_ANGLES.map((_, index) => index), (index) => canisterAt(CANISTER_ANGLES[index]!));

    return order.map((index) => canisterSvg(index) + feedLineOf(index)).join("") + solidSvg(cylinder(TANK[0], TANK[1], 5.6, LID_TOP, 5.4, P, 36, 0.4), {
        tone: "mid"
    });
};

export const SELECTOR_TOP = LID_TOP + 9.8;

export const selectorSvg = (degrees: number) => {
    const tip = onRing(TANK, 3.6, degrees, SELECTOR_TOP);

    return solidSvg(cylinder(TANK[0], TANK[1], 4.4, LID_TOP + 5.4, 4.4, P, 36, 0.5), {
        tone: "hi"
    }) + lineSvg(knurl(TANK[0], TANK[1], 4.4, LID_TOP + 5.8, SELECTOR_TOP - 0.4, 26, P), {
        tone: "lo"
    }) + lineSvg(lineOnTop(TANK, [
        tip[0],
        tip[1]
    ], SELECTOR_TOP, P), {
        tone: "lit"
    });
};

export const CANISTER_DEGREES = CANISTER_ANGLES;

const regulator = () => {
    const foot: Plan = {
        x: REGULATOR[0] - 7,
        y: REGULATOR[1] - 7,
        d: 14,
        w: 14,
        r: 2
    };

    return solidSvg(slabOf(foot, 0, 2.5, P, 3, 0.4), {
        tone: "lo"
    }) + corners(foot, 2.2).map(([
        x,
        y
    ]) => dotAt([
        x,
        y,
        2.5
    ])).join("") + solidSvg(cylinder(REGULATOR[0], REGULATOR[1], 7, 2.5, 13, P, 48, 0.4), {
        inner: lineSvg(sideArc(REGULATOR[0], REGULATOR[1], 7, 12.5, P, 32), {
            tone: "faint"
        }),
        tone: "mid"
    }) + solidSvg(cylinder(REGULATOR[0], REGULATOR[1], 4.4, 15.5, 4, P, 32), {
        tone: "mid"
    });
};

export const REGULATOR_TOP = 24;

export const regulatorKnobSvg = (degrees: number) => {
    const tip = onRing(REGULATOR, 4.2, degrees, REGULATOR_TOP);

    return solidSvg(cylinder(REGULATOR[0], REGULATOR[1], 5.4, 19.5, REGULATOR_TOP - 19.5, P, 40, 0.5), {
        tone: "hi"
    }) + lineSvg(knurl(REGULATOR[0], REGULATOR[1], 5.4, 19.9, REGULATOR_TOP - 0.4, 30, P), {
        tone: "lo"
    }) + lineSvg(lineOnTop(REGULATOR, [
        tip[0],
        tip[1]
    ], REGULATOR_TOP, P), {
        tone: "hi"
    });
};

const monitor = () => {
    const unit: Plan = {
        x: MONITOR_X - 28,
        y: -104,
        d: 44,
        w: 56,
        r: 5
    };

    const neck: Plan = {
        x: MONITOR_X - 7,
        y: -86,
        d: 10,
        w: 14,
        r: 2
    };

    const bezel = prismOf(frameOf([
        MONITOR_X,
        MONITOR_FACE - 7,
        34
    ], [
        0,
        1,
        0
    ], [
        1,
        0,
        0
    ], [
        0,
        0,
        1
    ]), roundedFace(128, 84, 5), 0, 7, P);

    return [
        solidSvg(slabOf(unit, 0, DOCK_TOP, P, 6, 0.6), {
            tone: "mid"
        }),
        corners(unit, 5).map(([
            x,
            y
        ]) => screwAt(x, y, DOCK_TOP)).join(""),
        solidSvg(slabOf(neck, DOCK_TOP, 48, P, 3, 0.4), {
            tone: "mid"
        }),
        solidSvg(bezel, {
            tone: "mid"
        }),
        solidSvg(disc(0, 2, 4.4, frameAlong([
            MONITOR_X,
            unit.y + unit.d,
            PIPE_Z
        ], [
            0,
            1,
            0
        ]), P), {
            tone: "mid"
        })
    ].join("");
};

const phone = () => {
    const dock: Plan = {
        y: PHONE_Y - 30,
        x: -94,
        d: 60,
        w: 38,
        r: 5
    };

    const rest: Plan = {
        y: PHONE_Y - 12,
        x: -90,
        d: 24,
        w: 8,
        r: 2
    };

    const body = prismOf(frameOf([
        PHONE_FACE - 6,
        PHONE_Y,
        DOCK_TOP
    ], [
        1,
        0,
        0
    ], [
        0,
        -1,
        0
    ], [
        0,
        0,
        1
    ]), roundedFace(50, 104, 8), 0, 6, P);

    return [
        solidSvg(slabOf(dock, 0, DOCK_TOP, P, 6, 0.6), {
            tone: "mid"
        }),
        corners(dock, 5).map(([
            x,
            y
        ]) => screwAt(x, y, DOCK_TOP)).join(""),
        solidSvg(slabOf(rest, DOCK_TOP, 50, P, 3, 0.4), {
            tone: "mid"
        }),
        solidSvg(body, {
            tone: "mid"
        }),
        solidSvg(disc(0, 2, 4.4, frameAlong([
            dock.x + dock.w,
            PHONE_Y,
            PIPE_Z
        ], [
            1,
            0,
            0
        ]), P), {
            tone: "mid"
        })
    ].join("");
};

const phoneLip = () => solidSvg(slabOf({
    y: PHONE_Y - 27,
    x: PHONE_FACE,
    r: 1.5,
    d: 54,
    w: 4
}, DOCK_TOP, 5, P, 3, 0.3), {
    tone: "mid"
});

function roundedFace(width: number, height: number, radius: number): Array<[number, number]> {
    return roundedPlan({
        x: -width / 2,
        d: height,
        r: radius,
        w: width,
        y: 0
    }, 8);
}

type ScreenFrame = {
    transform: string;
    outline: string;
    clip: string;
};

const monitorScreen = (): ScreenFrame => {
    const {
        height,
        width
    } = SCREEN_SIZES.monitor;

    const left = MONITOR_X - (width * UI_SCALE) / 2;

    const top = 34 + 6 + height * UI_SCALE;

    const plane = sideMatrix([
        0,
        MONITOR_FACE
    ], 0, P, "left");

    const shape = pathOf(roundedPlan({
        d: height * UI_SCALE,
        w: width * UI_SCALE,
        x: left,
        y: -top,
        r: 1.6
    }, 4), true);

    return {
        outline: `<g transform="${plane}"><path class="iso-line" data-tone="lo" d="${shape}"/></g>`,
        transform: `${plane} translate(${left} ${-top}) scale(${UI_SCALE})`,
        clip: `<path transform="${plane}" d="${shape}"/>`
    };
};

const phoneScreen = (): ScreenFrame => {
    const {
        height,
        width
    } = SCREEN_SIZES.phone;

    const left = -(PHONE_Y + (width * UI_SCALE) / 2);

    const top = DOCK_TOP + 3.4 + height * UI_SCALE;

    const plane = sideMatrix([
        PHONE_FACE,
        0
    ], 0, P, "right");

    const shape = pathOf(roundedPlan({
        d: height * UI_SCALE,
        w: width * UI_SCALE,
        x: left,
        y: -top,
        r: 5.6
    }, 6), true);

    return {
        outline: `<g transform="${plane}"><path class="iso-line" data-tone="lo" d="${shape}"/></g>`,
        transform: `${plane} translate(${left} ${-top}) scale(${UI_SCALE})`,
        clip: `<path transform="${plane}" d="${shape}"/>`
    };
};

export const SCREENS: Record<PlantDevice, ScreenFrame> = {
    monitor: monitorScreen(),
    phone: phoneScreen()
};

export type PipeArt = {
    length: number;
    under: string;
    width: number;
    over: string;
    core: string;
};

const pipeOf = (points: Array<Point3>, radius: number, caps: [boolean, boolean] = [
    true,
    true
]): PipeArt => {
    const route = points.length > 2 ? fillet(points, radius * 3.2, 9, {
        tube: radius,
        P
    }) : points;

    const pieces = tubePieces(route, radius, P, {
        maxLength: 400,
        caps
    });

    const flat = route.map((point) => iso(point, P));

    const length = flat.slice(1).reduce((sum, point, index) => sum + Math.hypot(point[0] - flat[index]![0], point[1] - flat[index]![1]), 0);

    return {
        over: pieces.map((piece) => `${piece.wide ? `<path class="plant-shine" d="${piece.shine}"/>` : ""}<path class="iso-line tb-edge" data-tone="hi" d="${piece.edges}"/>`).join(""),
        under: pieces.map((piece) => `<path class="tb-body plant-glass" d="${piece.body}"/>`).join(""),
        width: radius * 2 * K * 0.56,
        core: pathOf(flat),
        length
    };
};

const outletFace = add(onRing(TANK, TANK_RADIUS - 0.6, 45, PIPE_Z), DIAGONAL, 2.6);

export const PIPES = {
    feed: pipeOf([
        add([
            REGULATOR[0],
            REGULATOR[1],
            PIPE_Z
        ], DIAGONAL, 6.6),
        add(TEE, DIAGONAL, -3.9)
    ], MAIN_RADIUS),
    main: pipeOf([
        outletFace,
        add([
            REGULATOR[0],
            REGULATOR[1],
            PIPE_Z
        ], DIAGONAL, -6.6)
    ], MAIN_RADIUS),
    monitor: pipeOf([
        [
            TEE[0] + 3.9,
            TEE[1],
            PIPE_Z
        ],
        [
            MONITOR_X,
            TEE[1],
            PIPE_Z
        ],
        [
            MONITOR_X,
            -58,
            PIPE_Z
        ]
    ], BRANCH_RADIUS, [
        false,
        true
    ]),
    phone: pipeOf([
        [
            TEE[0],
            TEE[1] + 3.9,
            PIPE_Z
        ],
        [
            TEE[0],
            PHONE_Y,
            PIPE_Z
        ],
        [
            -54,
            PHONE_Y,
            PIPE_Z
        ]
    ], BRANCH_RADIUS, [
        false,
        true
    ])
};

const pedestalAt = (x: number, y: number, radius: number) => solidSvg(slabOf({
    x: x - 3.5,
    y: y - 3.5,
    r: 1.2,
    d: 7,
    w: 7
}, 0, PIPE_Z - radius, P, 3, 0.3), {
    tone: "mid"
});

export const PEDESTALS = {
    phone: [
        [
            TEE[0],
            24
        ],
        [
            -24,
            PHONE_Y
        ]
    ].map(([
        x,
        y
    ]) => pedestalAt(x!, y!, BRANCH_RADIUS)).join(""),
    monitor: [
        [
            24,
            TEE[1]
        ],
        [
            MONITOR_X,
            -32
        ]
    ].map(([
        x,
        y
    ]) => pedestalAt(x!, y!, BRANCH_RADIUS)).join("")
};

export const PLANT_ART = {
    tee: solidSvg(sphereOf(TEE, TEE_RADIUS, P), {
        tone: "mid"
    }),
    glassFront: glassFront(),
    canisters: canisters(),
    glassBack: glassBack(),
    regulator: regulator(),
    phoneLip: phoneLip(),
    monitor: monitor(),
    tank: tankBody(),
    phone: phone(),
    base: base(),
    lid: lid()
};

const TURRET_Z = 17.5;

const TURRET_RADIUS = 10;

const TURRET_FACE = TANK[1] + TANK_RADIUS + 6.4;

export const TURRET_WINDOW_RADII = [
    0.4,
    1,
    1.7
];

const turretPlane = sideMatrix([
    0,
    TURRET_FACE
], 0, P, "left");

const turret = () => {
    const boss = frameAlong([
        TANK[0],
        TANK[1] + TANK_RADIUS - 0.6,
        TURRET_Z
    ], [
        0,
        1,
        0
    ]);

    const index: Plan = {
        y: TANK[1] + TANK_RADIUS - 0.6,
        x: TANK[0] - 1.2,
        d: 7.2,
        w: 2.4,
        r: 0.4
    };

    const indexTop = TURRET_Z + TURRET_RADIUS + 1.4;

    return [
        solidSvg(disc(0, 4.6, 3.4, boss, P), {
            tone: "mid"
        }),
        solidSvg(disc(4.6, 7, TURRET_RADIUS, boss, P), {
            tone: "mid"
        }),
        solidSvg(slabOf(index, indexTop - 1.6, 1.6, P, 2, 0.2), {
            tone: "mid"
        }),
        lineSvg(lineOnTop([
            TANK[0],
            index.y
        ], [
            TANK[0],
            index.y + index.d
        ], indexTop, P), {
            tone: "lit"
        })
    ].join("");
};

/**
 * The turret face: three windows, one per sharpness, cut with that sharpness's corner.
 * The face turns as one piece, so the chosen window comes round under the index.
 */
export const turretFaceSvg = (degrees: number, active: number) => {
    const cx = TANK[0];

    const cz = -TURRET_Z;

    const windows = TURRET_WINDOW_RADII.map((radius, index) => {
        const angle = radians(-90 + index * 120);

        const x = cx + 5.6 * Math.cos(angle);

        const y = cz + 5.6 * Math.sin(angle);

        return `<rect class="plant-window" data-active="${index === active ? "" : undefined}" transform="rotate(${index * 120} ${x} ${y})" x="${x - 2.8}" y="${y - 1.7}" width="5.6" height="3.4" rx="${radius}"/>`;
    }).join("").replace(/ data-active="undefined"/g, "");

    const studs = Array.from({
        length: 12
    }, (_, index) => {
        const angle = radians(index * 30 + 15);

        return `<circle class="plant-stud" cx="${(cx + 8.4 * Math.cos(angle)).toFixed(2)}" cy="${(cz + 8.4 * Math.sin(angle)).toFixed(2)}" r="0.35"/>`;
    }).join("");

    return `<g transform="${turretPlane}"><g transform="rotate(${degrees.toFixed(2)} ${cx} ${cz})">${windows}${studs}</g><circle class="plant-hub" cx="${cx}" cy="${cz}" r="1.2"/></g>`;
};

const BOARD_CENTRE: Point3 = [
    96,
    96,
    33
];

const BOARD_TILT = radians(24);

const BOARD_NORMAL: Point3 = [
    Math.SQRT1_2 * Math.cos(BOARD_TILT),
    Math.SQRT1_2 * Math.cos(BOARD_TILT),
    Math.sin(BOARD_TILT)
];

const BOARD_RIGHT: Point3 = [
    Math.SQRT1_2,
    -Math.SQRT1_2,
    0
];

const BOARD_UP: Point3 = [
    -Math.SQRT1_2 * Math.sin(BOARD_TILT),
    -Math.SQRT1_2 * Math.sin(BOARD_TILT),
    Math.cos(BOARD_TILT)
];

const BOARD_WIDTH = 56;

const BOARD_HEIGHT = 58;

const BOARD_THICKNESS = 2.2;

const FOOT_TOP = 2.6;

const KNOB_RADIUS = 5.8;

const KNOB_HEIGHT = 1.8;

const NOTCH_HALF_ANGLE = 60;

const HEX_RADIUS = 16;

const Y_ARM = 5.4;

const onBoard = (x: number, y: number, s = 0): Point3 => add(add(add(BOARD_CENTRE, BOARD_RIGHT, x), BOARD_UP, y), BOARD_NORMAL, s);

/**
 * The SVG matrix of the board's face at depth s: local x runs to the right, local y runs down.
 */
const boardMatrix = (s: number) => {
    const origin = iso(onBoard(0, 0, s), P);

    const right = iso(onBoard(1, 0, s), P);

    const down = iso(onBoard(0, -1, s), P);

    return `matrix(${[
        right[0] - origin[0],
        right[1] - origin[1],
        down[0] - origin[0],
        down[1] - origin[1],
        origin[0],
        origin[1]
    ].map((value) => value.toFixed(4)).join(" ")})`;
};

const BOARD_SHADE = toneOf(BOARD_NORMAL, P);

const BOARD_FILL = BOARD_SHADE > 3 ? "class=\"iso-top\"" : `class="iso-shade" data-shade="${BOARD_SHADE}"`;

/**
 * The six knobs sit on the corners of a hexagon. Each one points its notch at the centre
 * when it is aligned, which is the angle away from its own corner.
 */
export const KNOB_ANGLES = [
    90,
    30,
    -30,
    -90,
    -150,
    150
];

export const ALIGNED_NOTCHES = KNOB_ANGLES.map((degrees) => degrees + 180);

const knobCentre = (index: number) => {
    const angle = radians(KNOB_ANGLES[index]!);

    return [
        HEX_RADIUS * Math.cos(angle),
        HEX_RADIUS * Math.sin(angle)
    ];
};

const logoBoard = () => {
    const foot = cylinder(BOARD_CENTRE[0] + 3, BOARD_CENTRE[1] + 3, 20, 0, FOOT_TOP, P, 64, 0.5);

    const posts = [
        -15,
        15
    ].map((x) => {
        const [
            px,
            py,
            pz
        ] = onBoard(x * 1.2, -BOARD_HEIGHT / 2, -BOARD_THICKNESS / 2);

        return solidSvg(cylinder(px, py, 1.5, FOOT_TOP, pz - FOOT_TOP, P, 16), {
            tone: "mid"
        });
    }).join("");

    const strutTop = onBoard(0, 6, -BOARD_THICKNESS);

    const strut = tubePieces([
        strutTop,
        [
            strutTop[0] - 12,
            strutTop[1] - 12,
            FOOT_TOP
        ]
    ], 1.1, P, {
        maxLength: 400
    }).map((piece) => `<g class="tb"><path class="tb-body" d="${piece.body}"/><path class="tb-shine" d="${piece.shine}"/><path class="iso-line tb-edge" data-tone="mid" d="${piece.edges}"/></g>`).join("");

    const board = prismOf(frameOf(onBoard(0, 0, -BOARD_THICKNESS), BOARD_NORMAL, BOARD_RIGHT, BOARD_UP), roundedPlan({
        y: -BOARD_HEIGHT / 2,
        x: -BOARD_WIDTH / 2,
        d: BOARD_HEIGHT,
        w: BOARD_WIDTH,
        r: 3
    }, 6), 0, BOARD_THICKNESS, P);

    const face = boardMatrix(0);

    const screws = [
        [
            -24.5,
            -25.5
        ],
        [
            24.5,
            -25.5
        ],
        [
            -24.5,
            25.5
        ],
        [
            24.5,
            25.5
        ]
    ].map(([
        x,
        y
    ]) => `<circle class="plant-board-screw" cx="${x}" cy="${y}" r="0.9"/>`).join("");

    const knobs = KNOB_ANGLES.map((_, index) => {
        const [
            x,
            y
        ] = knobCentre(index);

        return solidSvg(disc(0, KNOB_HEIGHT, KNOB_RADIUS, frameAlong(onBoard(x!, y!, 0), BOARD_NORMAL), P), {
            tone: "hi"
        });
    }).join("");

    return [
        solidSvg(foot, {
            tone: "mid"
        }),
        strut,
        posts,
        solidSvg(board, {
            tone: "mid"
        }),
        `<g transform="${face}">${screws}<text class="plant-board-label" x="0" y="25.4" text-anchor="middle">ncore</text></g>`,
        knobs
    ].join("");
};

export const logoCoreSvg = () => {
    const arms = [
        [
            -Math.cos(radians(30)),
            -0.5
        ],
        [
            Math.cos(radians(30)),
            -0.5
        ],
        [
            0,
            1
        ]
    ].map(([
        x,
        y
    ]) => `M0 0L${(x! * Y_ARM).toFixed(2)} ${(y! * Y_ARM).toFixed(2)}`).join("");

    return `<g transform="${boardMatrix(0)}"><path class="plant-logo-core" d="${arms}"/></g>`;
};

/**
 * The cube the rings only suggest: the hexagon round the six knobs and the three edges
 * that meet at its near corner. It lights up once every notch is in line.
 */
export const logoNeonSvg = () => {
    const corner = (degrees: number) => {
        const angle = radians(degrees);

        return [
            HEX_RADIUS * Math.cos(angle),
            -HEX_RADIUS * Math.sin(angle)
        ].map((value) => value.toFixed(2)).join(" ");
    };

    const hexagon = `M${KNOB_ANGLES.map(corner).join("L")}Z`;

    const spokes = [
        150,
        30,
        -90
    ].map((degrees) => `M0 0L${corner(degrees)}`).join("");

    return `<g transform="${boardMatrix(KNOB_HEIGHT + 0.05)}"><path class="plant-neon-tube" d="${hexagon}${spokes}"/><path class="plant-neon-core" d="${hexagon}${spokes}"/></g>`;
};

export const knobFacesSvg = (angles: Array<number>) => {
    const plane = boardMatrix(KNOB_HEIGHT);

    const reach = KNOB_RADIUS + 0.3;

    const half = radians(NOTCH_HALF_ANGLE);

    const faces = angles.map((degrees, index) => {
        const [
            x,
            y
        ] = knobCentre(index);

        const notch = `M0 0L${(reach * Math.cos(half)).toFixed(2)} ${(reach * Math.sin(half)).toFixed(2)}A${reach} ${reach} 0 0 0 ${(reach * Math.cos(half)).toFixed(2)} ${(-reach * Math.sin(half)).toFixed(2)}Z`;

        const edges = `M0 0L${(KNOB_RADIUS * Math.cos(half)).toFixed(2)} ${(KNOB_RADIUS * Math.sin(half)).toFixed(2)}M0 0L${(KNOB_RADIUS * Math.cos(half)).toFixed(2)} ${(-KNOB_RADIUS * Math.sin(half)).toFixed(2)}`;

        return `<g transform="translate(${x!.toFixed(2)} ${(-y!).toFixed(2)}) rotate(${(-degrees).toFixed(2)})"><circle class="plant-knob-face" r="${KNOB_RADIUS}"/><path ${BOARD_FILL} d="${notch}"/><circle ${BOARD_FILL} r="1.5"/><path class="plant-notch-edge" d="${edges}"/><circle class="plant-notch-edge" r="1.5"/></g>`;
    }).join("");

    return `<g transform="${plane}">${faces}</g>`;
};

const GAUGE_Z = 18;

const GAUGE_RADIUS = 7.4;

const GAUGE_MIN = 24;

const GAUGE_MAX = 48;

const GAUGE_SPAN = 270;

const GAUGE_FROM = 180;

export const GAUGES: Record<PlantDevice, Plane> = {
    phone: [
        TEE[0],
        44
    ],
    monitor: [
        44,
        TEE[1]
    ]
};

const gaugeBody = (at: Plane) => {
    const [
        x,
        y
    ] = at;

    const top = GAUGE_Z + 3.2;

    const ticks = radialTicks(x, y, GAUGE_RADIUS - 1.6, 6, 2, top, [
        1,
        2
    ], P, radians(GAUGE_FROM), radians(GAUGE_SPAN));

    return [
        solidSvg(cylinder(x, y, 1.3, PIPE_Z + BRANCH_RADIUS - 0.4, GAUGE_Z - (PIPE_Z + BRANCH_RADIUS - 0.4), P, 16), {
            tone: "mid"
        }),
        solidSvg(cylinder(x, y, GAUGE_RADIUS, GAUGE_Z, 3.2, P, 48, 0.5), {
            tone: "hi"
        }),
        lineSvg(ring(x, y, GAUGE_RADIUS - 1, top, P, 48), {
            tone: "lo"
        }),
        lineSvg(ticks.minor, {
            tone: "lo"
        }),
        lineSvg(ticks.major, {
            tone: "mid"
        })
    ].join("");
};

export const gaugeNeedleSvg = (device: PlantDevice, value: number) => {
    const [
        x,
        y
    ] = GAUGES[device];

    const share = (value - GAUGE_MIN) / (GAUGE_MAX - GAUGE_MIN);

    const angle = radians(GAUGE_FROM + share * GAUGE_SPAN);

    const dx = Math.sin(angle);

    const dy = -Math.cos(angle);

    const top = GAUGE_Z + 3.4;

    return lineSvg(lineOnTop([
        x - dx * 1.4,
        y - dy * 1.4
    ], [
        x + dx * (GAUGE_RADIUS - 2),
        y + dy * (GAUGE_RADIUS - 2)
    ], top, P), {
        tone: "lit"
    }) + lineSvg(ring(x, y, 0.9, top, P, 12), {
        tone: "hi"
    });
};

const checkValve = () => {
    const frame = frameAlong([
        -42,
        PHONE_Y,
        PIPE_Z
    ], [
        1,
        0,
        0
    ]);

    const capTop = PIPE_Z + 7.6;

    return solidSvg(solidOf([
        [
            0,
            3.4
        ],
        [
            1.2,
            4.2,
            1
        ],
        [
            5,
            5,
            1
        ],
        [
            8.8,
            4.2,
            1
        ],
        [
            10,
            3.4
        ]
    ], frame, P), {
        tone: "mid"
    }) + solidSvg(cylinder(-37, PHONE_Y, 2.4, PIPE_Z + 4.4, capTop - PIPE_Z - 4.4, P, 24, 0.3), {
        tone: "mid"
    }) + lineSvg(onTop([
        [
            -35.4,
            PHONE_Y - 1
        ],
        [
            -38.6,
            PHONE_Y
        ],
        [
            -35.4,
            PHONE_Y + 1
        ]
    ], capTop, P), {
        tone: "hi"
    });
};

const labelPlate = () => {
    const plan: Plan = {
        r: 1.2,
        x: -44,
        d: 11,
        w: 32,
        y: 94
    };

    return solidSvg(slabOf(plan, 0, 0.8, P, 3), {
        crease: "none",
        tone: "lo"
    }) + lineSvg([
        22,
        16,
        26
    ].map((length, index) => lineOnTop([
        plan.x + 4,
        plan.y + 3 + index * 2.6
    ], [
        plan.x + 4 + length,
        plan.y + 3 + index * 2.6
    ], 0.8, P)).join(""), {
        free: true,
        tone: "lo"
    }) + dotAt([
        plan.x + plan.w - 3,
        plan.y + plan.d / 2,
        0.8
    ]);
};

export const EXTRA_ART = {
    monitorGauge: gaugeBody(GAUGES.monitor),
    phoneGauge: gaugeBody(GAUGES.phone),
    checkValve: checkValve(),
    logoBoard: logoBoard(),
    label: labelPlate(),
    turret: turret()
};
